import React from 'react';
import cx from 'classnames';
import Modal from 'react-modal';
import invoke from 'lodash.invoke';
import {MediumDevice, SmallDevice} from '../MediaQueries';
import {BackButton, BackButtonDrawer} from '../BackButton';
import {ProcessingBar} from '../ProgressBar';

function safeSize ( size ) {
    const sizeMap = {
        xs: 'md:max-w-xs',
        sm: 'md:max-w-sm',
        md: 'md:max-w-md',
        lg: 'md:max-w-lg',
        xl: 'md:max-w-xl',
        '2xl': 'md:max-w-2xl',
        '3xl': 'md:max-w-3xl'
    };
    return sizeMap[size] ?? `max-w-full`;
}

export const ModalContext = React.createContext();

const MODAL_CONTAIN_VERT = 'flex h-full flex-col justify-center';
const MODAL_CONTAIN_HORZ = 'relative flex flex-col h-full md:h-auto md:py-12';
const MODAL_CONTENT = 'pointer-events-auto pt-14 md:pt-0 w-full h-full mx-auto bg-white md:shadow-1-strong md:rounded md:overflow-hidden md:static md:h-auto md:bg-transparent';
const DRAWER_CONTENT = 'pointer-events-auto fixed left-0 right-0 bottom-0 h-4/5 shadow-2 bg-white slide-up';
const MODAL_HEADER_MOBILE = 'translate--y z-10 w-full flex justify-between items-center fixed text-base bg-white p-4 border-b border-solid border-grey-lighter';
const MODAL_HEADER_DESKTOP = 'modal-header flex w-full items-center justify-between';
const MODAL_PRINT_ONLY = 'modal-print-only';
const NO_SCROLL = 'no-scroll';

const renderAction = (
    labels = {
        backAction: 'Back',
        drawerAction: 'Done'
    },
    isDrawer,
    onClose ) =>
    isDrawer
    ? <BackButtonDrawer onClick={onClose}>{labels.drawerAction}</BackButtonDrawer>
    : <BackButton onClick={onClose}>{labels.backAction}</BackButton>

const ModalHeader = ( {labels, title, drawer, isClosable, onClose, className} ) =>
    <>
        <SmallDevice>
            <header className={cx( MODAL_HEADER_MOBILE, className )}>
                {isClosable
                 ? renderAction( labels, drawer, onClose )
                 : <span className="flex-0 w-8"/>
                }
                <h2 className="subheading-base px-4 ellipsis">{title}</h2>
                <span className="flex-0 w-8"/>
            </header>
        </SmallDevice>
        <MediumDevice>
            <header className={cx( MODAL_HEADER_DESKTOP, className )}>
                <h2 className="headline-sm">{title}</h2>
                {isClosable &&
                    <span
                        onClick={onClose}
                        className="cursor-pointer inline-block w-10 ml-4 -mr-2 leading-none text-center text-black text-3xl">
                    &times;
                </span>
                }
            </header>
        </MediumDevice>
    </>

const UPIModal = ( {
                       children,
                       id = 'UPI_MODAL',
                       refOverlay = null,
                       disablePageScrolling = true,
                       labels,
                       ...props
                   } ) => {
    const onClose = () => invoke( props, 'onClose', id );
    const isClosable = typeof props.onClose === 'function';
    const modalTitle = props.title || '';
    const sizeClass = safeSize( props.size );

    const modalEl = React.useRef( {} );

    const SlotHeader = props.slotHeader ?? ModalHeader;

    React.useEffect( () => {

        if ( props.isOpen ) {
            // re-append Modal.node to it's parent so current "Active" modal is always top layer
            const modalNode = modalEl.current.node;
            if ( modalNode ) {
                modalNode.parentNode.appendChild( modalNode );
            }

            const modalWrap = document.querySelector( '.ReactModal__Overlay' );
            // reset scroll on modal
            if ( modalWrap && modalWrap.scrollTo ) {
                modalWrap.scrollTo( 0, 0 );
            }

            // prevent scrolling on body
            disablePageScrolling && document.documentElement.classList.add( NO_SCROLL );
            props.printOnly && document.documentElement.classList.add( MODAL_PRINT_ONLY );
        }
        else {
            document.documentElement.classList.remove( NO_SCROLL, MODAL_PRINT_ONLY );
        }

        return () => {
            document.documentElement.classList.remove( NO_SCROLL, MODAL_PRINT_ONLY );
        }
    }, [props.isOpen] );

    const contentStyles = props.drawer ? DRAWER_CONTENT : cx( `${sizeClass}`, MODAL_CONTENT );
    const headerStyles = cx(
        {'translate--y': props.drawer},
        props.headerClassName
    );
    const bodyStyles = cx( sizeClass,
        'modal-body overflow-y-auto w-full mx-auto relative',
        {'absolute-center top-0 h-full': props.drawer},
        props.contentClassName
    )
    // <div className="flex-auto"> need to be set to height:100vh for mobile
    return (
        <ModalContext.Provider value={{onClose, backButtonLabel: props.backButtonLabel}}>
            <Modal
                id={id}
                ref={modalEl}
                appElement={document.body}
                onRequestClose={onClose}
                className={cx( `${sizeClass}`, 'mx-auto h-full pointer-events-none' )}
                {...props}>
                <div data-testid={id} className="flex-auto h-screen md:h-auto">
                    <div className={MODAL_CONTAIN_VERT}>
                        <div className={MODAL_CONTAIN_HORZ}>
                            <div className={contentStyles}>
                                <SlotHeader title={modalTitle}
                                            labels={labels}
                                            drawer={props.drawer}
                                            isClosable={isClosable}
                                            onClose={onClose}
                                            className={headerStyles}/>
                                {props.processing && <ProcessingBar/>}
                                <section className={bodyStyles}>
                                    {children}
                                </section>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </ModalContext.Provider>
    );
}

UPIModal.displayName = 'UPIModal';

export default UPIModal;
